import { useQuery } from '@tanstack/react-query';
import { Box, Flex, Heading, Stack, Text, chakra } from '@chakra-ui/react';
import { ChartNoAxesCombined, Loader2 } from 'lucide-react';
import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useState } from 'react';

import { getMovers } from '../api/client';
import type { TableDensity } from '../hooks/useDashboardControls';
import { useDisplayedMovers } from '../hooks/useDisplayedMovers';
import { filterMovers, type DashboardFilters } from '../lib/filters';
import { FilterBar } from './FilterBar';
import { LiveMoversTable } from './LiveMoversTable';

export function MoversView({
  filters,
  onFiltersChange,
  autoRefresh,
  density,
}: {
  filters: DashboardFilters;
  onFiltersChange: Dispatch<SetStateAction<DashboardFilters>>;
  autoRefresh: boolean;
  density: TableDensity;
}) {
  const [visibleLimit, setVisibleLimit] = useState(50);
  const filterKey = JSON.stringify(filters);

  useEffect(() => {
    setVisibleLimit(50);
  }, [filterKey]);

  const moversQuery = useQuery({
    queryKey: ['movers-full', filters, visibleLimit],
    queryFn: ({ signal }) => getMovers(filters, { signal, limit: visibleLimit }),
    refetchInterval: 2_000,
  });
  const rawMovers = moversQuery.data?.items ?? [];
  const movers = filterMovers(rawMovers, filters);
  const snapshot = useDisplayedMovers(movers, {
    autoRefresh,
    intervalSeconds: 5,
    resetKey: filterKey,
  });
  const canShowMore = snapshot.displayed.length >= visibleLimit;

  return (
    <Stack as="section" mt="8" gap="5">
      <Flex
        flexDir={{ base: 'column', xl: 'row' }}
        gap="3"
        align={{ xl: 'flex-end' }}
        justify={{ xl: 'space-between' }}
      >
        <Stack gap="3">
          <Flex align="center" gap="3">
            <Box color="signal.blue">
              <ChartNoAxesCombined size={28} />
            </Box>
            <Heading as="h1" fontSize="3xl" fontWeight="semibold" letterSpacing="tight">
              Movers
            </Heading>
          </Flex>
          <Text fontSize={{ base: 'sm', md: 'md' }} color="text.muted">
            Per-market drill-down of the events on the dashboard. Grouping happens on the Dashboard tab.
          </Text>
        </Stack>
        {moversQuery.isFetching ? (
          <Box
            display="inline-flex"
            alignItems="center"
            gap="2"
            borderRadius="oqPanel"
            px="4"
            py="2.5"
            layerStyle="glassPanel"
          >
            <Box as="span" display="inline-flex" alignItems="center">
              <Loader2 className="oq-spin" size={16} />
            </Box>
            <Text as="span" fontSize="sm" color="text.muted">
              Refreshing
            </Text>
          </Box>
        ) : null}
      </Flex>

      <FilterBar filters={filters} onFiltersChange={onFiltersChange} />

      {moversQuery.isError ? (
        <Box
          mt="3"
          borderWidth="1px"
          borderColor="signal.warn/20"
          bg="signal.warn/10"
          px="4"
          py="3"
          borderRadius="oqPanel"
        >
          <Text fontSize="sm" color="signal.warn">
            Movers are unavailable from the backend right now.
          </Text>
        </Box>
      ) : null}

      <Box mt="5">
        <LiveMoversTable
          movers={snapshot.displayed}
          isLoading={moversQuery.isLoading}
          mode="expanded"
          rowCount={visibleLimit}
          density={density}
        />
      </Box>

      {canShowMore ? (
        <Flex mt="4" justify="center">
          <chakra.button
            type="button"
            onClick={() => {
              setVisibleLimit((value) => value + 50);
            }}
            borderRadius="oqPanel"
            px="5"
            py="3"
            fontSize="sm"
            fontWeight="semibold"
            color="text.muted"
            transition="all 0.2s"
            layerStyle="glassPanel"
            _hover={{ color: 'text.primary' }}
          >
            Show more movers
          </chakra.button>
        </Flex>
      ) : null}
    </Stack>
  );
}